import ParameterValidator from '@mvps-genesis/parameter-validator';
import {Tabs} from '@mvps-genesis/accessibility';
import EventBus from '@mvps-genesis/event-bus';

/*
 *--------------------------------------------------------------------------
 * Tabbed Carousel
 *--------------------------------------------------------------------------
 *
 * Class containing functionality for the Tabbed Carousel C&L component.
 *
 */
export default class TabbedCarousel {

	/**
	 * Parameter Validator class object
	 *
	 * @var	{ParameterValidator} @mvps-genesis/parameter-validator
	 */
	#validator;

	/**
	 * Tabs class object
	 *
	 * @var	{Tabs} @mvps-genesis/accessibility
	 */
	#tabs;

	/**
	 * Root container element
	 *
	 * @var {HTMLElement}
	 */
	containerElement;

	/**
	 * Active tab class name
	 *
	 * @var {String}
	 */
	activeClass;

	/**
	 * Tab overflow class name. Used for horizontal overflow detection and styling.
	 *
	 * @var {String}
	 */
	tabOverflowClass;

	/**
	 * Create a TabbedCarousel instance.
	 *
	 * @param  {HTMLElement|String}  containerElement           The root container element.
	 * @param  {String}              options.activeClass        Active tab class name. Defaults to 'g-tabbed-carousel__tab--active'.
	 * @param  {Boolean}             options.autoActivatePanels Auto activate the panels on tab focus. Defaults to true.
	 * @param  {String}              options.tabOverflowClass   Tab overflow class name. Used for horizontal overflow detection and styling. Defaults to 'has-overflow'.
	 */
	constructor (
		containerElement,
		{
			activeClass = 'g-tabbed-carousel__tab--active',
			autoActivatePanels = true,
			tabOverflowClass = 'has-overflow'
		} = {}
	) {
		this.#validator = new ParameterValidator(this);
		this.containerElement = this.#validator.isElement('containerElement', containerElement);
		this.#tabs = new Tabs(this.containerElement, {autoActivate: autoActivatePanels});
		this.activeClass = this.#validator.isString('activeClass', activeClass);
		this.tabOverflowClass = this.#validator.isString('tabOverflowClass', tabOverflowClass);

		this.observeTabList();
		this.bindEvents();
	}

	/**
	 * Set observers for the tab list element.
	 */
	observeTabList () {
		const tablistResizeObserver = new ResizeObserver(([entry]) => {
			this.setTabListOverflow(entry.target);
		});

		tablistResizeObserver.observe(this.#tabs.tabListElement);
	}

	/**
	 * Bind the tab event listeners.
	 */
	bindEvents () {
		EventBus.addEventListener('MVPS:activeTab', this.onActiveTab.bind(this));
	}

	/**
	 * Active tab event handling.
	 *
	 * @param  {Event} event Active tab event object.
	 */
	onActiveTab (event) {
		if (event.detail.tabListElement !== this.#tabs.tabListElement) {
			return;
		}

		const activeTabElement = event.detail.tabElement;

		for (const tabItem of this.#tabs.tabItems) {
			tabItem.tab.classList.toggle(this.activeClass, tabItem.tab === activeTabElement);
		}

		const carouselContainerElements = event.detail.tabPanelElement.getElementsByClassName('js-carousel');

		for (const carouselContainerElement of carouselContainerElements) {
			if (carouselContainerElement.data && carouselContainerElement.data.carousel) {
				carouselContainerElement.data.carousel.refresh();
			} else {
				const observer = new MutationObserver((mutations, me) => {
					if (carouselContainerElement.data && carouselContainerElement.data.carousel) {
						me.disconnect();

						carouselContainerElement.data.carousel.refresh();
					}
				});

				observer.observe(carouselContainerElement, {
					childList: true
				});
			}
		}
	}

	/**
	 * Handle tab list overflow functionality.
	 *
	 * @param {HTMLElement} tabListElement The tab list element.
	 */
	setTabListOverflow (tabListElement) {
		this.#tabs.tabListElement.classList.toggle(this.tabOverflowClass, tabListElement.scrollWidth > tabListElement.clientWidth);
	}

	/**
	 * Get the Tabs class instance - @mvps-genesis/accessibility
	 *
	 * @return {Tabs} The Tabs class instance - @mvps-genesis/accessibility
	 */
	getTabsInstance () {
		return this.#tabs;
	}
}
